<template>
  <div class="game-step-prizes-table" data-qa="prizes-list-wrapper">
    <rest-list
      ref="rest-list"
      :loading="isLoading"
      :payload="fetchEndpoint"
      :data-handler="extractData"
      :empty-text="$t('Gamification_No_Prizes_Added')"
      data-qa="prizes-rest-list"
      @dataLoaded="setDataLoaded"
    >
      <rest-list-column
        prop="name"
        :title="$t('Gamification_Name')"
        :min-width="100"
        data-qa="column-prize-name"
      />

      <rest-list-column
        :title="$t('Gamification_Start_Date')"
        prop="starts_at"
        :sortable="true"
        :is-range="false"
        :filter-date-range="false"
        :filter-date="true"
        :date-picker-props="datePickerProps"
        data-qa="column-start-date"
      >
        <template v-slot:default="scope">
          <p v-if="scope.row.starts_at">
            {{ castDate(scope.row.starts_at) }}
          </p>
        </template>
      </rest-list-column>

      <rest-list-column
        :title="$t('Gamification_End_Date')"
        prop="ends_at"
        :sortable="true"
        :is-range="false"
        :filter-date-range="false"
        :filter-date="true"
        :date-picker-props="datePickerProps"
        data-qa="column-end-date"
      >
        <template v-slot:default="scope">
          <p v-if="scope.row.ends_at">
            {{ castDate(scope.row.ends_at) }}
          </p>
        </template>
      </rest-list-column>

      <rest-list-column
        :title="
          isLeaderboard
            ? $t('Gamification_Rank_Min')
            : $t('Gamification_Points_Min')
        "
        prop="range_from"
        :min-width="100"
        data-qa="column-min"
      />
      <rest-list-column
        :title="
          isLeaderboard
            ? $t('Gamification_Rank_Max')
            : $t('Gamification_Points_Max')
        "
        prop="range_to"
        :min-width="100"
        data-qa="column-max"
      />

      <rest-list-column-actions v-if="!isReadOnly" data-qa="column-actions">
        <template slot="header" slot-scope="{}">
          <div class="settings">
            <icon class="settings-icon" glyph="cog" size="20" />
          </div>
        </template>

        <template slot-scope="scope">
          <rest-list-action
            :click="() => editRow(scope.row)"
            icon="el-icon-edit-outline"
            :title="$t('Gamification_View_Edit')"
            data-qa="btn-edit-prize"
          />
          <!-- @todo Hiding because gamify backend is not working  -->
          <rest-list-action
            :click="() => duplicateRow(scope.row.id)"
            icon="el-icon-document-copy"
            :title="$t('Gamification_Duplicate_Prize')"
            data-qa="btn-duplicate-prize"
            v-if="false"
          />
          <rest-list-action-delete
            :endpoint="rowDeleteEndpoint(scope.row.id)"
            :name="scope.row.name"
          />
        </template>
      </rest-list-column-actions>
    </rest-list>

    <div v-if="isCreationMode" class="footer" data-qa="prizes-footer">
      <div class="footer-content" data-qa="footer-content">
        <el-button
          type="primary"
          :disabled="isNextStepButtonDisabled"
          @click="goToNextStep()"
          data-qa="next-step-btn"
        >
          {{ $t('Gamification_Step_4_Submit_Button_Label') }}
          <i class="el-icon-arrow-right el-icon-right"></i>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '@/core/EventBus'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestList from '@/common/components/rest/RestList.vue'
import RestListAction from '@/common/components/rest/RestListAction.vue'
import RestListColumn from '@/common/components/rest/RestListColumn.vue'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions.vue'
import { GameCreationSubSteps } from '@/modules/gamification/enum/GameCreationStepsEnum.js'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete.vue'
import RestApi from '@/core/services/RestApi'
import { castDate, clearFiltersAndSort } from '@/modules/gamification/helpers'
import { isEqual } from 'lodash'

export default {
  name: 'GameStepPrizesList',
  components: {
    RestListActionDelete,
    RestListColumnActions,
    RestListColumn,
    RestList,
    RestListAction
  },

  props: {
    segments: {
      type: Array,
      default: () => []
    },
    gameCampaignId: {
      type: Number
    },
    isLeaderboard: {
      type: Boolean
    },
    isCreationMode: {
      type: Boolean,
      required: true
    },
    saveFlag: {
      type: Boolean,
      required: true
    },
    isReadOnly: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      isLoading: false,
      prizesData: [],
      prizesEndpoint: null
    }
  },

  created() {
    this.prizesEndpoint = projectEndpoint({
      method: 'get',
      url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/prizes`
    })
  },

  computed: {
    fetchEndpoint() {
      return this.prizesEndpoint
    },

    isNextStepButtonDisabled() {
      return !this.prizesData.length
    },

    datePickerProps() {
      return {
        type: 'datetime',
        format: 'yyyy-MM-dd HH:mm',
        value_format: 'yyyy-MM-dd HH:mm',
        default_time: ['00:00:00']
      }
    }
  },

  watch: {
    saveFlag: {
      handler() {
        if (this.isCreationMode) {
          this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
        }
      }
    }
  },

  methods: {
    castDate,
    extractData(responseData) {
      return responseData.data || []
    },

    goToNextStep() {
      EventBus.$emit('campaign-create-next-step')
      EventBus.$emit('updateStep', GameCreationSubSteps.PRIZES)
    },

    editRow(row) {
      this.$emit('edit-prize', row)
    },

    async duplicateRow(prizeId) {
      RestApi(this.cloneEndpoint(prizeId))
        .then(() => {
          this.$message({
            message: this.$t('Cloned successfully'),
            type: 'success'
          })
          this.$refs['rest-list'].refresh()
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    rowDeleteEndpoint(prizeId) {
      return projectEndpoint({
        method: 'delete',
        url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/prize/${prizeId}`
      })
    },

    cloneEndpoint(prizeId) {
      return projectEndpoint({
        method: 'post',
        url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/clone-prize/${prizeId}`
      })
    },

    refresh() {
      this.$refs['rest-list'].refresh()
    },

    setDataLoaded(response) {
      this.prizesData = response.data
      this.$emit('loaded-prizes-count', response.data.length)
    }
  },

  beforeDestroy() {
    const newQuery = clearFiltersAndSort(this.$route.query)

    if (!isEqual(newQuery, this.$route.query)) {
      this.$router.replace({ query: newQuery })
    }
  }
}
</script>

<style scoped>
.game-step-prizes-table {
  padding-bottom: 60px;
}

.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}

.footer-content {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/prizes/GameStepPrizesList.vue